import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * App-level error boundary. Catches render-time errors anywhere below it so a single broken
 * component shows a recoverable message instead of a blank white screen. Class component because
 * React error boundaries cannot be written as hooks.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for debugging. Deliberately no email content / tokens are ever passed here.
    // eslint-disable-next-line no-console
    console.error("Render error caught by ErrorBoundary:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background px-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full text-left">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-destructive/10 text-destructive mb-5">
            <AlertTriangle className="w-5 h-5" aria-hidden="true" />
          </div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-muted-foreground leading-6">
            A part of the app ran into an unexpected error. You can try again, or reload the page.
            Your data is safe.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <Button onClick={this.handleReset} data-testid="error-boundary-retry">
              <RotateCw className="w-4 h-4 mr-2" aria-hidden="true" />
              Try again
            </Button>
            <Button variant="outline" onClick={this.handleReload} data-testid="error-boundary-reload">
              Reload page
            </Button>
          </div>
          {this.state.error?.message ? (
            <p className="mt-6 text-xs font-mono text-muted-foreground/70 break-words">
              {String(this.state.error.message).slice(0, 200)}
            </p>
          ) : null}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
